import { sortPeopleNames } from './people';
import { generatePdfBlobViaServer } from './pdf';
import { showTypeProfile } from './showTypes';
import { t, pdfLocale } from './pdfI18n';
import type { ShowDTO, GuestDTO, RoadmapItemDTO, ShowContactDTO, RoomingDTO, AccommodationDTO, MemberFlightDTO, ShowTechNoteDTO, ShowMiscNoteDTO, ShowCategorizedNoteDTO, VenueDTO } from '../api';
import { createDefaultDaysheetLayout, mergeDaysheetLayouts, type DaysheetLayout, type DaysheetSectionId, daysheetTableWidths, DAYSHEET_TABLE_COLUMNS, daysheetTableColumns } from './daysheetLayout';

interface DaysheetInput {
  show: ShowDTO;
  roadmap: RoadmapItemDTO[];
  guests: GuestDTO[];
  contacts?: ShowContactDTO[];
  rooming?: RoomingDTO[];
  accommodations?: AccommodationDTO[];
  flights?: MemberFlightDTO[];
  distances?: Array<{ from_label: string; to_label: string; mode?: string; distance?: string; duration?: string; notes?: string }>;
  techNotes?: ShowTechNoteDTO[];
  miscNotes?: ShowMiscNoteDTO[];
  categorizedNotes?: ShowCategorizedNoteDTO[];
  bandName?: string;
  venueName?: string;
  /** The venue's full record (name, address, city, zip, country, phone, email,
   *  website, parking, electricity, timezone). Rendered in the "venue" daysheet
   *  section. Added 2026-10-11; `venueName` stays for back-compat. */
  venue?: VenueDTO | null;
  /** Optional Checklists V2-B payload: the show's linked checklists. The
   *  manager PDF passes ALL items; a personal variant passes a filtered copy. */
  checklists?: Array<{ name?: string; category?: string; items?: Array<{ text?: string; done?: boolean; assignee?: string }> }>;
  /** Photo « document » de la salle (déjà résolue en data URL PNG/JPEG par
   *  l'appelant via `resolveImageToDataUrl`) + sa légende. Absente ⇒ la section
   *  n'est pas imprimée (une salle sans photo ne produit pas de page blanche). */
  venuePhoto?: { dataUrl?: string; caption?: string } | null;
  /** Qui est sur la route pour cette date (nom affiché + position), déjà résolu
   *  par l'appelant via `crewRowsFromAssignments` (src/lib/people.ts). Alimente
   *  la section « BAND AND CREW ». Vide ⇒ la section n'est pas imprimée. */
  people?: Array<{ name: string; position?: string }>;
  /** Band members used to resolve member_ids → NAMES in the flights table
   *  (2026-10-18: the itinerary preview printed raw UUIDs). Absent = raw ids. */
  members?: { id: string; name: string; /** Nom passeport (`NOM, Prénom`) : utilisé par
   *  le rooming et les vols, jamais le nom de scène (2026-11-03). */ officialName?: string }[];
}
export type { DaysheetInput };

/** Default uppercase headings, in the exact wording of the original generator. */
const SECTION_HEADINGS: Record<DaysheetSectionId, string> = {
  header: '',
  'general-info': 'GENERAL INFORMATION',
  venue: 'VENUE',
  'venue-photo': 'VENUE PHOTO',
  travel: 'TRAVEL',
  'band-crew': 'BAND AND CREW',
  contacts: 'CONTACT PERSONS',
  hotel: 'HOTEL',
  rooming: 'ROOMING LIST',
  'run-of-show': 'TIMINGS',
  guestlist: 'GUESTLIST',
  flights: 'FLIGHTS',
  distances: 'DISTANCES',
  notes: 'NOTES',
  'tech-notes': 'TECH NOTES',
  'misc-notes': 'LOGISTICS NOTES',
  'categorized-notes': 'NOTES',
};

// Build a pdfmake docDefinition for a show daysheet (run-of-show + key info),
// laid out after the SAMAEL itinerary example (GENERAL INFORMATION block).
//
// The layout (optional) drives which sections are emitted, in what order, with
// custom headings, fixed texts (title suffix / footer / header) and style.
// When omitted, buildDaysheetDoc reproduces the original hard-coded behaviour.
export function buildDaysheetDoc(input: DaysheetInput, layout?: DaysheetLayout) {
  const { show, roadmap, guests, contacts, rooming, accommodations, flights, distances, techNotes, miscNotes, categorizedNotes, bandName, venueName, venue, people, venuePhoto } = input;
  const dateStr = show.date ? new Date(show.date).toLocaleDateString(pdfLocale(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'TBD';
  const sorted = [...roadmap].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  // Effective layout (layout is already the merged effective value from the
  // server; merging again over the defaults is idempotent and back-compatible).
  const L = mergeDaysheetLayouts(createDefaultDaysheetLayout(), layout ?? null);
  const S = L.style;
  const T = L.texts;

  const secTitle = (id: DaysheetSectionId): string => {
    const sec = L.sections.find((s) => s.id === id);
    if (sec?.title && sec.title.trim()) return sec.title.trim();
    const def = SECTION_HEADINGS[id];
    return def ? t('pdf.ds.section.' + id) : '';
  };

  const heading = (id: DaysheetSectionId, margin: [number, number, number, number] = [0, 8, 0, 4]): any => {
    const sec = L.sections.find((s) => s.id === id);
    return { text: secTitle(id), style: 'h2', margin, ...(sec?.pageBreakBefore ? { pageBreak: 'before' as const } : {}) };
  };

  // Largeurs de colonnes ÉDITABLES : les poids du layout (ou les défauts du
  // moteur) normalisés en pourcentages. Une seule source : `daysheetLayout.ts`.
  const colWidths = (id: DaysheetSectionId): string[] =>
    daysheetTableWidths(id, L.sections.find((s) => s.id === id)?.columnWidths);

  // ---- Section builders (each returns Content[] | null; null auto-hides) ----
  const venueLine = (venueName || show.venue_name || '').trim(); // '' ⇒ pas de salle, rien à afficher
  const buildHeader = (): any[] => {
    const titleLine = [bandName || show.band_name || 'Show', T.titleSuffix].filter(Boolean).join(' ');
    const subTitle = secTitle('header') || show.name || 'Show';
    return [
      { text: titleLine, style: 'band' },
      { text: subTitle, style: 'title', margin: [0, 0, 0, 8] },
      {
        // La salle n'est affichée QUE si elle est spécifiée : un jour de voyage / studio /
        // off n'a pas de salle, et « Venue: TBD » n'apprend rien (2026-11-03).
        columns: venueLine
          ? [
            { width: '*', text: t('pdf.ds.date', { value: dateStr }) },
            { width: '*', text: t('pdf.ds.venue', { value: venueLine }) },
          ]
          : [{ width: '*', text: t('pdf.ds.date', { value: dateStr }) }],
        margin: [0, 0, 0, 4],
      },
      {
        columns: [
          { width: '*', text: t('pdf.ds.status', { value: show.status || t('pdf.ds.status.scheduled') }) },
          { width: '*', text: t('pdf.ds.type', { value: show.event_type || 'show' }) },
        ],
        margin: [0, 0, 0, 12],
      },
    ];
  };

  // General information (the SAMAEL itinerary's GENERAL INFORMATION block)
  const buildGeneralInfo = (): any[] | null => {
    // PROFIL PAR TYPE (2026-10-17, lib/showTypes.ts) : doors / stage name sans sens
    // sur un travel day / day off / studio — masqués dans le PDF aussi.
    const dsHidden = new Set(showTypeProfile(show.event_type).daySheetHidden || []);
    const genInfo: { label: string; value?: string }[] = [];
    if (show.welcome_note) genInfo.push({ label: t('pdf.ds.welcome'), value: show.welcome_note });
    if (show.timezone) genInfo.push({ label: t('pdf.ds.timezone'), value: show.timezone });
    if (show.door_time && !dsHidden.has('doors')) genInfo.push({ label: t('pdf.ds.doors'), value: show.door_time });
    if (show.electricity) genInfo.push({ label: t('pdf.ds.electricity'), value: show.electricity });
    if (show.wifi_venue) genInfo.push({ label: t('pdf.ds.wifi'), value: show.wifi_venue });
    if (show.currency_note) genInfo.push({ label: t('pdf.ds.currency'), value: show.currency_note });
    if (show.weather) genInfo.push({ label: t('pdf.ds.weather'), value: show.weather });
    if (show.currency_rate) genInfo.push({ label: t('pdf.ds.rate'), value: show.currency_rate });
    if (show.luggage_notes) genInfo.push({ label: t('pdf.ds.luggage'), value: show.luggage_notes });
    if (show.duration_minutes) genInfo.push({ label: t('pdf.ds.duration'), value: t('pdf.ds.durationMinutes', { count: show.duration_minutes }) });
    if (show.stage_size) genInfo.push({ label: t('pdf.ds.stage'), value: show.stage_size });
    if (show.stage_name && !dsHidden.has('stageName')) genInfo.push({ label: t('pdf.ds.stageName'), value: show.stage_name });
    if (show.artist_pass) genInfo.push({ label: t('pdf.ds.artistPass'), value: show.artist_pass });
    if (show.passport_reminder) genInfo.push({ label: t('pdf.ds.passport'), value: show.passport_reminder });

    if (genInfo.length === 0) return null;
    const cell = (pair: { label: string; value?: string }) => ({
      stack: [
        { text: pair.label, bold: true, fontSize: S.labelFontSize, color: S.labelColor },
        { text: pair.value || '', fontSize: S.bodyFontSize, margin: [0, 2, 0, 0] },
      ],
    });
    const rows: any[][] = [];
    for (let i = 0; i < genInfo.length; i += 2) {
      const a = genInfo[i];
      const b = genInfo[i + 1];
      rows.push([cell(a), b ? cell(b) : { text: '' }]);
    }
    return [
      heading('general-info'),
      { table: { widths: ['50%', '50%'], body: rows }, layout: 'noBorders', margin: [0, 0, 0, 8] },
    ];
  };

  // Venue (the venue record — name, address, contact, parking, tech). Shared by
  // every show that plays at the venue. Hidden when the venue is unknown / has
  // no data at all.
  const buildVenue = (): any[] | null => {
    if (!venue) return null;
    const v = venue || {};
    const cityZip = [v.city, v.zip].filter(Boolean).join(' / ');
    const pairs: { label: string; value?: string }[] = [];
    if (v.name) pairs.push({ label: t('pdf.ds.venueInfo.name'), value: v.name });
    if (v.address) pairs.push({ label: t('pdf.ds.venueInfo.address'), value: v.address });
    if (cityZip) pairs.push({ label: t('pdf.ds.venueInfo.cityZip'), value: cityZip });
    if (v.country) pairs.push({ label: t('pdf.ds.venueInfo.country'), value: v.country });
    if (v.phone) pairs.push({ label: t('pdf.ds.venueInfo.phone'), value: v.phone });
    if (v.email) pairs.push({ label: t('pdf.ds.venueInfo.email'), value: v.email });
    if (v.website) pairs.push({ label: t('pdf.ds.venueInfo.website'), value: v.website });
    if (v.parking) pairs.push({ label: t('pdf.ds.venueInfo.parking'), value: v.parking });
    if (v.electricity) pairs.push({ label: t('pdf.ds.venueInfo.electricity'), value: v.electricity });
    if (v.timezone) pairs.push({ label: t('pdf.ds.venueInfo.timezone'), value: v.timezone });
    if (pairs.length === 0) return null;
    const cell = (pair: { label: string; value?: string }) => ({
      stack: [
        { text: pair.label, bold: true, fontSize: S.labelFontSize, color: S.labelColor },
        { text: pair.value || '', fontSize: S.bodyFontSize, margin: [0, 2, 0, 0] },
      ],
    });
    const rows: any[][] = [];
    for (let i = 0; i < pairs.length; i += 2) {
      const a = pairs[i];
      const b = pairs[i + 1];
      rows.push([cell(a), b ? cell(b) : { text: '' }]);
    }
    return [
      heading('venue'),
      { table: { widths: ['50%', '50%'], body: rows }, layout: 'noBorders', margin: [0, 0, 0, 8] },
    ];
  };

  // Contact persons
  const buildContacts = (): any[] | null => {
    if (!contacts || contacts.length === 0) return null;
    return [
      heading('contacts'),
      {
        table: {
          widths: colWidths('contacts'),
          body: [
            [{ text: t('pdf.ds.col.role'), bold: true }, { text: t('pdf.ds.col.name'), bold: true }, { text: t('pdf.ds.col.phone'), bold: true }, { text: t('pdf.ds.col.email'), bold: true }],
            ...contacts.map((c) => [{ text: c.role || '' }, { text: c.name || '' }, { text: c.phone || '' }, { text: c.email || '' }]),
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 8],
      },
    ];
  };

  // Hotel (accommodations)
  const buildHotel = (): any[] | null => {
    if (!accommodations || accommodations.length === 0) return null;
    return [
      heading('hotel'),
      {
        table: {
          widths: colWidths('hotel'),
          body: [
            [{ text: t('pdf.ds.col.hotel'), bold: true }, { text: t('pdf.ds.col.address'), bold: true }, { text: t('pdf.ds.col.phone'), bold: true }, { text: t('pdf.ds.col.conf'), bold: true }],
            ...accommodations.map((a) => [
              { text: a.hotel_name || '' },
              { text: [a.address, a.zip, a.city, a.country].filter(Boolean).join(', ') || '' },
              { text: a.phone || '' },
              { text: a.confirmation_number || '' },
            ]),
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 8],
      },
    ];
  };

  // Venue photo — la photo « document » de la salle, pleine largeur + légende.
  // Le champ `venue-photo.image` porte l'image ; `venue-photo.caption` la légende.
  // Pas de photo ⇒ null (aucune page, aucun titre orphelin).
  const buildVenuePhoto = (): any[] | null => {
    const dataUrl = (venuePhoto?.dataUrl || '').trim();
    if (!dataUrl) return null;
    const caption = (venuePhoto?.caption || '').trim();
    const blocks: any[] = [
      heading('venue-photo'),
      { image: dataUrl, width: 500, alignment: 'center', margin: [0, 4, 0, 6] },
    ];
    if (caption) blocks.push({ text: caption, italics: true, fontSize: 9, color: '#666', alignment: 'center', margin: [0, 0, 0, 8] });
    return blocks;
  };

  // Band and crew — qui est sur la route pour cette date (nom affiché + position).
  /**
   * TRAVEL (2026-11-03) — l'axe du jour : FROM → TO + moyen de transport.
   * Un travel day n'a pas de salle ; c'est la première information utile du
   * document. Les horaires, eux, restent dans FLIGHTS (les jambes de vol) :
   * ici on imprime le TRAJET, pas la logistique.
   * La section s'auto-masque dès qu'il n'y a rien à dire (ni from, ni to, ni
   * moyen) — un jour de concert normal ne l'imprime donc jamais.
   */
  const buildTravel = (): any[] | null => {
    const from = (show.from_city || '').trim();
    const to = (show.to_city || '').trim();
    const meansKey = (show.transport || '').trim().toLowerCase();
    const means = meansKey ? t(`pdf.ds.transport.${meansKey}`) : '';
    // Il faut un TRAJET (from ou to) : un show a souvent un `transport` (bus/avion)
    // sans être un jour de voyage — sinon la section imprimerait « — — Van » sur
    // une date de concert (vécu 2026-11-03 avec le festival 558).
    if (!from && !to) return null;
    return [
      heading('travel'),
      {
        table: {
          widths: colWidths('travel'),
          body: [
            [
              { text: t('pdf.ds.col.from'), bold: true },
              { text: t('pdf.ds.col.to'), bold: true },
              { text: t('pdf.ds.col.means'), bold: true },
            ],
            [{ text: from || '—' }, { text: to || '—' }, { text: means || '—' }],
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 8],
      },
    ];
  };

  // Aucune ligne ⇒ pas de section (comme le rooming) : un document n'imprime pas
  // un tableau vide. Le contrat nom/position vit dans src/lib/people.ts.
  const buildBandCrew = (): any[] | null => {
    const rows = (people || []).filter((p) => (p?.name || '').trim());
    if (rows.length === 0) return null;
    return [
      heading('band-crew'),
      {
        table: {
          widths: colWidths('band-crew'),
          body: [
            [{ text: t('pdf.ds.col.name'), bold: true }, { text: t('pdf.ds.col.position'), bold: true }],
            ...rows.map((p) => [{ text: p.name }, { text: p.position || '' }]),
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 8],
      },
    ];
  };

  // Rooming list
  const buildRooming = (): any[] | null => {
    if (!rooming || rooming.length === 0) return null;
    return [
      heading('rooming'),
      {
        table: {
          widths: colWidths('rooming'),
          body: [
            [{ text: t('pdf.ds.col.name'), bold: true }, { text: t('pdf.ds.col.room'), bold: true }, { text: t('pdf.ds.col.hotel'), bold: true }],
            ...rooming.map((rm) => {
              const hotel = accommodations?.find((a) => a.id === rm.accommodation_id);
              // 2026-11-03 : le nom de la liste des chambres vient du REGISTRE (identité
              // passeport). `role_at_date` n'est qu'un libellé de secours pour une
              // personne inconnue — c'était la source principale, d'où les « Johi » /
              // « Yvan » imprimés au lieu du nom du passeport.
              const known = (input.members || []).some((mm) => String(mm.id) === String(rm.team_member_id));
              return [
                { text: known ? memberName(String(rm.team_member_id)) : (rm.role_at_date || '') },
                { text: rm.room_number || (rm.room_comment || '') },
                { text: hotel?.hotel_name || '' },
              ];
            }),
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 8],
      },
    ];
  };

  // Run of show (schedule) — always present (emits a placeholder when empty).
  const buildRunOfShow = (): any[] => {
    const blocks: any[] = [heading('run-of-show')];
    if (sorted.length === 0) {
      blocks.push({ text: t('pdf.ds.noSchedule'), italics: true, color: '#888' });
    } else {
      blocks.push({
        table: {
          widths: colWidths('run-of-show'),
          body: [
            [{ text: t('pdf.ds.col.time'), bold: true }, { text: t('pdf.ds.col.activity'), bold: true }, { text: t('pdf.ds.col.responsible'), bold: true }],
            ...sorted.map((it) => [
              { text: [it.time_from, it.time_to].filter(Boolean).join(' – ') || (it as RoadmapItemDTO & { time?: string }).time || '' },
              { text: it.activity },
              { text: it.responsible || '' },
            ]),
          ],
        },
        layout: 'lightHorizontalLines',
      });
    }
    return blocks;
  };

  // Guestlist (if any)
  const buildGuestlist = (): any[] | null => {
    if (guests.length === 0) return null;
    return [
      heading('guestlist', [0, 12, 0, 4]),
      {
        table: {
          widths: colWidths('guestlist'),
          body: [
            [{ text: t('pdf.ds.col.name'), bold: true }, { text: t('pdf.ds.col.role'), bold: true }, { text: t('pdf.ds.col.status'), bold: true }],
            ...guests.map((g) => [{ text: g.name }, { text: g.role || '' }, { text: g.check_in_status || 'pending' }]),
          ],
        },
        layout: 'lightHorizontalLines',
      },
    ];
  };

  // Notes
  const buildNotes = (): any[] | null => {
    if (!show.notes) return null;
    return [
      heading('notes', [0, 12, 0, 4]),
      { text: show.notes },
    ];
  };

  // Tech notes (tech_notes table) — one block per note.
  const buildTechNotes = (): any[] | null => {
    if (!techNotes || techNotes.length === 0) return null;
    return [
      heading('tech-notes', [0, 12, 0, 4]),
      ...techNotes.map((n) => ({ text: n.content || '' })),
    ];
  };

  // Misc / logistics notes (misc_notes table) — category + content per note.
  const buildMiscNotes = (): any[] | null => {
    if (!miscNotes || miscNotes.length === 0) return null;
    return [
      heading('misc-notes', [0, 12, 0, 4]),
      ...miscNotes.map((n) => [
        ...(n.category ? [{ text: n.category, bold: true }] : []),
        { text: n.content || '' },
      ]),
    ];
  };

  // Categorized notes (notes table) — category + title + content per note.
  const buildCategorizedNotes = (): any[] | null => {
    if (!categorizedNotes || categorizedNotes.length === 0) return null;
    return [
      heading('categorized-notes', [0, 12, 0, 4]),
      ...categorizedNotes.map((n) => [
        ...(n.title ? [{ text: n.title, bold: true }] : []),
        ...(n.category ? [{ text: n.category, italics: true, color: '#666' }] : []),
        { text: n.content || '' },
      ]),
    ];
  };

  // Checklists (V2-B) — the show's linked checklists with their items. The
  // manager PDF receives ALL items (the caller passes them pre-filtered when a
  // personal variant is needed, e.g. the per-member email daysheet).
  const buildChecklists = (): any[] | null => {
    const cls = input.checklists || [];
    const blocks: any[] = [];
    for (const cl of cls) {
      const items = cl.items || [];
      if (items.length === 0) continue;
      blocks.push({ text: cl.name || t('pdf.cl.checklist'), bold: true, margin: [0, 4, 0, 2] });
      blocks.push({
        ul: items.map((it) => ({
          text: `${it.done ? '[x]' : '[ ]'} ${it.text || ''}${it.assignee ? ` — ${it.assignee}` : ''}`,
        })),
        margin: [0, 0, 0, 6],
      });
    }
    if (blocks.length === 0) return null;
    // Literal heading (NOT heading('checklist'): that registry is typed to
    // DaysheetSectionId and old saved layouts must stay untouched).
    return [
      { text: t('pdf.cl.checklist'), style: 'h2', margin: [0, 12, 0, 4] },
      ...blocks,
    ];
  };

  // Flights (per booking — people on each row) + the flights Notes text.
  // Nom imprimé pour une réservation (vol, rooming) : l'identité PASSEPORT d'abord,
  // le nom affiché ensuite, l'id seulement en dernier recours (jamais un UUID si la
  // personne est connue).
  const memberName = (id: string): string => {
    const m = (input.members || []).find((mm) => String(mm.id) === String(id));
    return m?.officialName || m?.name || id;
  };
  const withIata = (city: string | undefined, iata: string | undefined): string =>
    [city, iata ? `(${iata})` : ''].filter(Boolean).join(' ');
  const buildFlights = (): any[] | null => {
    if (!flights || flights.length === 0) return null;
    return [
      heading('flights', [0, 12, 0, 4]),
      {
        table: {
          // PEOPLE got its width bounded (2026-10-18): a row with 8 members used
          // to stretch the table and squeeze Time·Duration onto two lines.
          widths: colWidths('flights'),
          body: [
            [{ text: t('pdf.ds.col.people'), bold: true }, { text: t('pdf.ds.col.from'), bold: true }, { text: t('pdf.ds.col.to'), bold: true }, { text: t('pdf.ds.col.flight'), bold: true }, { text: t('pdf.ds.col.timeDuration'), bold: true }],
            ...flights.map((f) => {
              let people: string[] = [];
              try { people = JSON.parse(f.member_ids || '[]'); } catch { people = []; }
              // Legacy single member fallback.
              if (people.length === 0 && f.team_member_id) people = [f.team_member_id];
              // Manual "other" names (person_name) appended to the checked members.
              const other = (f.person_name || '').trim();
              const allPeople = other ? [...people, ...other.split(',').map((s) => s.trim()).filter(Boolean)] : people;
              // Passagers triés A→Z (identité passeport, insensible aux accents).
              const names = sortPeopleNames(allPeople.map(memberName));
              // TRAIN legs (SPEC-TRAIN-LEG-FIXES 2026-10-20): the From/To cells
              // fall back to the station name (train legs store from/to_city
              // empty when a station is set), and the FLIGHT column carries the
              // train details (operator, class, coach, seat, platform).
              const isTrain = f.mode === 'train';
              const route = (city?: string, iata?: string, station?: string) =>
                isTrain ? (station || city || '') : withIata(city, iata);
              const flightLabel = isTrain
                ? [f.airline, f.flight_no, f.booking_ref, f.train_class, f.coach, f.seat, f.platform].filter((x) => (x || '').trim()).join(' ')
                : [f.airline, f.flight_no, f.booking_ref].filter(Boolean).join(' ');
              const timeLabel = [f.departure, f.arrival].filter(Boolean).join(' – ');
              const withDuration = [timeLabel, f.duration].filter(Boolean).join(' · ');
              return [
                // Une personne par LIGNE (2026-11-03) : une liste de 8 noms sur une
                // seule ligne rendait la colonne illisible et écrasait les autres.
                { text: names.join('\n') },
                { text: route(f.from_city, f.from_iata, f.from_station) },
                { text: route(f.to_city, f.to_iata, f.to_station) },
                { text: flightLabel || '' },
                { text: withDuration || '' },
              ];
            }),
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 6],
      },
      // Flights notes (free text) directly under the table.
      ...(show.flight_notes ? [{ text: show.flight_notes, italics: true, color: '#555', margin: [0, 2, 0, 8] }] : []),
    ];
  };

  // Distances (airport→hotel, hotel→venue…) — structured rows shown in the daysheet.
  const buildDistances = (): any[] | null => {
    if (!distances || distances.length === 0) return null;
    return [
      heading('distances', [0, 8, 0, 4]),
      {
        table: {
          widths: colWidths('distances'),
          body: [
            [{ text: t('pdf.ds.col.from'), bold: true }, { text: t('pdf.ds.col.to'), bold: true }, { text: t('pdf.ds.col.mode'), bold: true }, { text: t('pdf.ds.col.distance'), bold: true }, { text: t('pdf.ds.col.duration'), bold: true }],
            ...distances.map((d) => [
              { text: d.from_label || '' },
              { text: d.to_label || '' },
              { text: d.mode || '' },
              { text: d.distance || '' },
              { text: d.duration || '' },
            ]),
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 8],
      },
    ];
  };

  /**
   * Applique le layout à un TABLEAU : ordre des colonnes, colonnes décochées,
   * libellé d'en-tête surchargé, largeurs (poids normalisés en %). Les builders
   * continuent de produire TOUTES les colonnes dans l'ordre historique — cette
   * passe est la seule à décider ce qui s'imprime, donc une colonne masquée ne
   * peut pas réapparaître ailleurs.
   */
  const applyColumnLayout = (sid: DaysheetSectionId, engine: any[]): any[] => {
    const defs = DAYSHEET_TABLE_COLUMNS[sid] || [];
    if (!defs.length) return engine;
    const sec = L.sections.find((x) => x.id === sid);
    const cols = daysheetTableColumns(sec, sid);
    const indexOf = new Map(defs.map((d, i) => [d.id, i]));
    const total = cols.reduce((a, c) => a + c.weight, 0) || 1;
    const widths = cols.map((c) => `${((c.weight / total) * 100).toFixed(2)}%`);
    return engine.flatMap((node) => {
      if (!node || !node.table) return [node];
      if (cols.length === 0) return []; // toutes les colonnes décochées ⇒ rien
      const body: any[][] = Array.isArray(node.table.body) ? node.table.body : [];
      if (!body.length) return [node];
      const next = body.map((row, r) => {
        if (r === 0) {
          return cols.map((c) => {
            const idx = indexOf.get(c.id);
            const base = idx != null ? row[idx] : null;
            const label = c.labelOverride || (base && base.text) || '';
            return { ...(base || {}), text: label, bold: true };
          });
        }
        return cols.map((c) => {
          const idx = indexOf.get(c.id);
          return idx != null ? row[idx] : { text: '' };
        });
      });
      return [{ ...node, table: { ...node.table, widths, body: next } }];
    });
  };

  // ---- Layout-driven emission ---- (spacer/text fields + field ordering)
  const builders: Record<DaysheetSectionId, () => any[] | null> = {
    header: buildHeader,
    travel: buildTravel,
    'general-info': buildGeneralInfo,
    venue: buildVenue,
    'venue-photo': buildVenuePhoto,
    'band-crew': buildBandCrew,
    contacts: buildContacts,
    hotel: buildHotel,
    rooming: buildRooming,
    'run-of-show': buildRunOfShow,
    guestlist: buildGuestlist,
    flights: buildFlights,
    distances: buildDistances,
    notes: buildNotes,
    'tech-notes': buildTechNotes,
    'misc-notes': buildMiscNotes,
    'categorized-notes': buildCategorizedNotes,
  };
  // Field id helpers: '…\.spacer' → gap, '…\.text' → free-text paragraph.
  const isSpacerField = (fid: string): boolean => fid === 'spacer' || fid.endsWith('.spacer');
  const isTextField = (fid: string): boolean => fid === 'text' || fid.endsWith('.text');
  const spacerNode = (size: number): any => ({
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: 0, y2: 0, lineWidth: 0 }],
    margin: [0, Math.max(0, size) / 2, 0, Math.max(0, size) / 2],
  });
  const textNode = (f: any): any | null => {
    const content = (f.textContent ?? '').trim();
    if (!content) return null;
    return {
      text: content,
      ...(f.bold ? { bold: true } : {}),
      ...(f.italic ? { italics: true } : {}),
      ...(f.fontSize ? { fontSize: f.fontSize } : {}),
      ...(f.align && f.align !== 'left' ? { alignment: f.align } : {}),
      margin: [0, 4, 0, 4],
    };
  };
  // Emit a section's fields in the layout's order. Spacer/text fields render
  // inline at their position; any other field id (e.g. 'general-info.welcome',
  // 'contacts.table') stands for the section's engine content, which is emitted
  // WHOLE at the position of its first engine field (so a spacer placed after
  // the table really comes after it). If the section has no fields list, the
  // engine block is emitted as-is (back-compat).
  const emitSection = (id: DaysheetSectionId): any[] => {
    const sec = L.sections.find((s) => s.id === id);
    const fields = sec?.fields && sec.fields.length > 0 ? sec.fields : null;
    const builtEngine = (builders[id] ? builders[id]() : null) || [];
    const engine = applyColumnLayout(id, builtEngine);
    if (!fields) return engine;
    const out: any[] = [];
    let engineEmitted = false;
    for (const f of fields) {
      if (!f.enabled) continue;
      if (isSpacerField(f.id)) { out.push(spacerNode(Math.max(0, Math.min(60, Number(f.size) || 4)))); continue; }
      if (isTextField(f.id)) { const n = textNode(f); if (n) out.push(n); continue; }
      // First engine field encountered → emit the whole engine block here.
      if (!engineEmitted && engine.length > 0) {
        out.push(...engine);
        engineEmitted = true;
      }
    }
    // A section with only spacer/text fields still shows its content at the end.
    if (!engineEmitted && engine.length > 0) out.push(...engine);
    return out;
  };

  const content: any[] = [];
  for (const sec of L.sections) {
    if (!sec.enabled) continue;
    const built = emitSection(sec.id as DaysheetSectionId);
    if (built.length) content.push(...built);
  }

  // Checklists (V2-B) — appended after the layout sections (not part of the
  // DaysheetSectionId registry, so old saved layouts stay untouched). Always
  // on: the daysheet has carried the run checklist since V1; the multi-list
  // model feeds it via input.checklists.
  {
    const built = buildChecklists();
    if (built && built.length) content.push(...built);
  }

  // Running page header / footer (off by default so the original look is kept).
  //
  // The runner travels to the server as a SERIALIZABLE `headerSpec`/`footerSpec`
  // descriptor: pdfmake pagination requires a FUNCTION and JSON.stringify would
  // silently drop it (F2). Only the tokens the client can resolve are filled
  // here; {page}/{pages} are left literal for the server, which rebuilds the
  // pdfmake function and substitutes them at render time.
  const hasFooter = !!(T.footerLeftTemplate || T.footerCenterTemplate || T.footerRightTemplate);
  const bandLabel = bandName || show.band_name || 'Show';
  const venueLabel = venueName || show.venue_name || '';
  const runnerFill = (tpl: string): string =>
    (tpl || '').replace(/\{(\w+)\}/g, (_, k: string) => {
      if (k === 'bandName') return bandLabel;
      if (k === 'venueName') return venueLabel;
      return `{${k}}`; // {page}/{pages} … resolved server-side
    });
  const headerFooterMargin: [number, number, number, number] = [S.pageMargins[0], 10, S.pageMargins[2], 0];
  const runnerSpec = (left: string, center: string, right: string) => ({
    left: runnerFill(left),
    center: runnerFill(center),
    right: runnerFill(right),
    margin: headerFooterMargin,
    fontSize: S.footerFontSize,
    color: S.footerColor,
  });

  return {
    content,
    styles: {
      band: { fontSize: S.titleFontSize, bold: true, color: S.titleColor },
      title: { fontSize: S.subtitleFontSize, color: S.subtitleColor },
      h2: { fontSize: S.headingFontSize, bold: true, color: S.headingColor },
    },
    defaultStyle: { fontSize: S.bodyFontSize, color: S.textColor },
    pageSize: 'A4',
    pageMargins: S.pageMargins,
    ...(T.headerEnabled
      ? { headerSpec: runnerSpec(T.headerLeftTemplate, T.headerCenterTemplate, T.headerRightTemplate) }
      : {}),
    // Footer: the layout can switch it off explicitly (footerEnabled absent =
    // enabled), on top of the 'at least one template filled' rule.
    ...(hasFooter && T.footerEnabled !== false
      ? { footerSpec: runnerSpec(T.footerLeftTemplate, T.footerCenterTemplate, T.footerRightTemplate) }
      : {}),
  };
}

export async function exportDaysheetPdf(input: DaysheetInput, layout?: DaysheetLayout): Promise<{ url: string; docDef: unknown }> {
  const docDef = buildDaysheetDoc(input, layout);
  const url = await generatePdfBlobViaServer(docDef as any);
  return { url, docDef };
}
